import { useEffect, useMemo, useRef, useState } from "react";
import CurrentCaseRequired from "../components/ui/current-case-required.js";
// import ReportCategorySankeyAnalysis from '../components/ui/report-category-sankey-analysis.js';
import { TransactionViewer } from "../components/ui/TransactionViewer.js";
import TransactionNodeChart from "../components/ui/TransactionNodeChart.js";
// import TransactionNetworkVisualization from "../components/ui/TransactionNetworkVisualization.js";
// import PDFReportGenerator from "../components/ui/PDFReportGenerator.js";
import { useGetEnrichedTransactions, flattenInfiniteData, ENRICHED_PAGE_LIMIT } from '../api/transactions.js';
import usePDFPrintTransactions from "../components/ui/PDFPrintTransactions.js";
import DataFilter from '../components/ui/filter/filtercontext.js';
import DateRangeFilter from '../components/ui/filter/filterdaterange.js';
import MultiSelectFilter from '../components/ui/filter/filtermultiselect.js';
import TextSearchFilter from '../components/ui/filter/filtertextsearch.js';
import DependentMultiSelectPairFilter from '../components/ui/filter/filterdependentmultiselectpair.js';
// import NumericRangeFilter from '../components/ui/filter/filternumericrange.js';
import CurrencyRangeFilter from '../components/ui/filter/filtercurrencyrange.js';
import TriStateBooleanFilter from "../components/ui/filter/filtertristateboolean.js";
import WithheldFlagsFilter from "../components/ui/filter/filterwithheldflags.js";
import { Box } from "@mui/material";
import TransactionDisplayOptionsForm from "../components/ui/TransactionDisplayOptionsForm.js";
import CollapsiblePanel from "../components/ui/CollapsiblePanel.js";
import CaseDatabaseCheck from "../components/ui/case-database-check.js";
import { perfLog, perfNow } from "../utils/perfLog.js";

export default function TransactionAnalysisReportPage(props) {
    // useGetEnrichedTransactions returns a useSWRInfinite Page[] ({ data: [...] }
    // per page). Flatten to a single enriched-transaction array — every other
    // consumer (Sankey, net-worth, drilldown hook, estate export) already does
    // this. This page was the lone exception: it fed the raw Page[] into the
    // filter/table, which both mis-shaped the data and made DataFilter's
    // full-array deepEqual walk page-wrapped objects. Memoize so the reference is
    // stable across renders where the underlying pages didn't change.
    // §4.8 withheld review: the enriched read adds the stage's :SUPPRESSED
    // flag rows only when asked (include_withheld); the "Withheld flags"
    // control in the filter panel below owns both halves and reports its
    // state through setIncludeWithheld, so the fetch opt-in and the narrowing
    // can never disagree. Off is the default feed — byte-identical to before.
    const [includeWithheld, setIncludeWithheld] = useState(false);
    const { data: rawTransactionPages, isLoading, mutate } = useGetEnrichedTransactions(true, ENRICHED_PAGE_LIMIT, includeWithheld);
    const transactions = useMemo(
        () => flattenInfiniteData(rawTransactionPages),
        [rawTransactionPages]
    );

    // Diagnostic-only (gated, primitives only): total wall time from page mount
    // to the full feed being loaded, plus page count and row total. Logs once.
    const mountTsRef = useRef(perfNow());
    const fetchLoggedRef = useRef(false);
    useEffect(() => {
        if (!isLoading && !fetchLoggedRef.current && transactions.length > 0) {
            fetchLoggedRef.current = true;
            perfLog('page.fetchComplete', {
                pages: Array.isArray(rawTransactionPages) ? rawTransactionPages.length : 0,
                rows: transactions.length,
                sinceMountMs: +(perfNow() - mountTsRef.current).toFixed(1),
            });
        }
    }, [isLoading, transactions, rawTransactionPages]);

    const [filteredTransactions, setFilteredTransactions] = useState(transactions);
    const [filtersDescription, setFiltersDescription] = useState("");
    // Sort order in words, captioned on the printed report.
    const [sortDescription, setSortDescription] = useState("");
    const [displayOptions, setDisplayOptions] = useState({
        isDraft: true,
        showMerchant: true,
        showSubcategory: true,
        showFlag: true,
        showNotes: true,
        maxNodesPerType: 10
    });


    const handleFilteredDataChange = (newFilteredData) => {
        setFilteredTransactions(newFilteredData);
    };

    // Mirror the table's sort order so PDF print emits rows in the order the
    // user is viewing. TransactionViewer pushes its sorted memo up via
    // onSortedTransactionsChange. Falls back to filteredTransactions before
    // the first emit (e.g., printing before the table has rendered).
    const [sortedPrintableTransactions, setSortedPrintableTransactions] = useState([]);
    const printableTransactions = sortedPrintableTransactions.length > 0
        ? sortedPrintableTransactions
        : filteredTransactions;

    const handlePrint = usePDFPrintTransactions({ transactions: printableTransactions, displayOptions: displayOptions, filtersDescription: filtersDescription, sortDescription: sortDescription });
    // console.log('filtersDescription = ', filtersDescription);


    return (
            <CaseDatabaseCheck>
        <CurrentCaseRequired>
            <button
                onClick={handlePrint}
                style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    marginBottom: '24px' // Added vertical spacing
                }}
            >
                🖨️ Print / Save as PDF
            </button>

            <TransactionDisplayOptionsForm
                displayOptions={displayOptions}
                setDisplayOptions={setDisplayOptions}
            />

            {/* Filter Section - Compact and Fixed */}
            <Box sx={{
                flexShrink: 0,
                overflow: 'visible',
                zIndex: 1
            }}>
                <DataFilter
                    data={transactions}
                    onFilteredDataChange={handleFilteredDataChange}
                    onFiltersDescriptionChange={setFiltersDescription}
                    title="Filter Transactions"
                    defaultCollapsed={true}
                >
                    <DateRangeFilter
                        field="transactionDate"
                        label="Transaction Date"
                    />

                    <MultiSelectFilter
                        field='bankAccountName'
                        label="Bank Account"
                    />

                    <TextSearchFilter
                        fields={['description']}
                        label="Description"
                    />

                    <CurrencyRangeFilter
                        field="amount"
                        label="Amount"
                        unit="$"
                    />

                    <DependentMultiSelectPairFilter
                        primaryField='categoryName'
                        secondaryField='subcategoryName'
                        primaryLabel="Category"
                        secondaryLabel="Subcategory"
                        label="Transaction Category"
                    />

                    <MultiSelectFilter
                        field='merchantName'
                        label="Merchant"
                    />

                    <MultiSelectFilter
                        field='flagName'
                        label="Flag"
                    />

                    {/* Contextual flag assessment §4.8: narrow to the flags the
                        case assessment withheld (and, with them, the §4.6
                        one-click restore on each row). */}
                    <WithheldFlagsFilter onIncludeChange={setIncludeWithheld} />


                    <TriStateBooleanFilter
                        field="hasTransferPartner"
                        label="Internal Transfers"
                        allLabel="All"
                        trueLabel="Only"
                        falseLabel="Exclude"
                    />
                </DataFilter>
            </Box>
            {/* Visualizations */}
            <Box sx={{ flexShrink: 0 }}>
                <CollapsiblePanel title="Transaction Network Visualization" defaultCollapsed={true}>
                    <TransactionNodeChart transactions={filteredTransactions} isLoading={isLoading} maxNodesPerType={displayOptions.maxNodesPerType} />
                </CollapsiblePanel>
            </Box>
            {/* Transaction table: fixed height so virtual scroller has room,
                flexShrink: 0 so it isn't squeezed when panels above expand.
                contentArea's overflow-y handles page-level scrolling. */}
            <Box sx={{
                flexShrink: 0,
                height: '70vh',
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
            }}>
                <TransactionViewer
                    transactions={filteredTransactions}
                    isLoading={isLoading}
                    displayOptions={displayOptions}
                    // The bound SWR mutate over the Page[] feed — the shape
                    // every edit hook (category/merchant/flag/notes and the
                    // §4.6 flag restore) needs to run its optimistic update.
                    mutateTransactions={mutate}
                    onSortedTransactionsChange={setSortedPrintableTransactions}
                    onSortDescriptionChange={setSortDescription}
                />
            </Box>
        </CurrentCaseRequired>
        </CaseDatabaseCheck>
    );
}